import cn from "classnames"
import Select, { components, type MenuListProps, type Props } from "react-select"
import Scrollable from "@/common/components/scrollable"
import env from "@/common/env"

const MenuList = ({ children, ...props }: MenuListProps<any, false, any>) => (
  <Scrollable>
    <components.MenuList {...props}>{children}</components.MenuList>
  </Scrollable>
)

interface CustomSelectProps {
  /** Left align option text */
  leftAlign?: boolean
}

interface Option {
  label: string
  value: any
}

const calcHeight = (options: number, bottom: number) => {
  const maxHeight = Math.min(800, window.innerHeight - 80)
  const bottomSpace = document.documentElement.clientHeight - bottom - 20

  if (bottomSpace >= 220) {
    return Math.round(Math.min(options * 44, bottomSpace))
  }

  const height = Math.min(Math.max(options * 44, 90), maxHeight)

  return Math.round(height)
}

const nativeLabel = (label: string) => {
  if (label.includes(" mm")) {
    return label.replace(/\s|mm/g, "")
  }

  if (label.includes(" in")) {
    return label.replace(/\s|in/g, "")
  }

  return label
}

const NativeSelect = <OptionType extends Option>(props: Props<OptionType, false> & CustomSelectProps) => {
  const options = props.options as OptionType[]
  const defaultValue = props.defaultValue as OptionType
  const value = props.value as OptionType
  const selected = value || defaultValue || { label: "" }
  const onChange = props.onChange ? (props.onChange as (value?: OptionType) => void) : null

  return (
    <select
      id={props.id}
      onChange={e => {
        if (onChange) {
          const label = e.target.value
          onChange(options.find(opt => nativeLabel(opt.label) === label))
        }
      }}
      value={selected ? nativeLabel(selected.label) : undefined}
    >
      {options.map(option => (
        <option key={option.label} value={nativeLabel(option.label)}>
          {nativeLabel(option.label)}
        </option>
      ))}
    </select>
  )
}

const SelectBox = <OptionType extends Option>(props: Props<OptionType, false> & CustomSelectProps) => {
  const { leftAlign, className, classNamePrefix } = props

  return (
    <Select
      {...props}
      className={cn("react-select", className)}
      classNamePrefix={cn("react-select", classNamePrefix)}
      styles={{
        control: base => ({
          ...base,
          pointerEvents: "auto",
        }),
        option: styles => {
          const textAlign = leftAlign ? "left" : "center"
          return { ...styles, textAlign }
        },
        menu: styles => {
          return {
            ...styles,
            position: "static",
            top: "initial",
            height: "100%",
          }
        },
        menuPortal: (styles, { rect, offset, position }: any) => {
          const height = calcHeight(props.options?.length || 0, rect.top + rect.height)
          let top: string | number = "initial"
          let bottom: string | number = 30

          if (document.documentElement.clientHeight - offset >= height + 20) {
            top = Math.round(rect.top + rect.height - 5)
            bottom = "initial"
          }

          return {
            left: Math.round(rect.left),
            top,
            bottom,
            position,
            width: Math.round(rect.width),
            zIndex: 3000,
            height,
          }
        },
      }}
      components={{ MenuList }}
      menuPortalTarget={document.querySelector("#select-portal") as HTMLElement}
      captureMenuScroll={false}
    />
  )
}

export default env.TEST ? NativeSelect : SelectBox
